import { useCallback, useEffect, useState } from 'react';
import {
  Sparkles, Phone, Plus, X, Loader2, Bot, PhoneCall, PlayCircle,
  AlertTriangle, CheckCircle2, Power, MessageSquare, Bell, TrendingUp, Eye, EyeOff, Shield, Ban, Webhook, Copy, BookOpen,
  Download,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { formatDateTime } from '@/lib/dateFormat';
import { toast } from '@/components/ui/toast';
import api from '@/lib/api';
import { ConfirmModal } from '@/components/ConfirmModal';
import { TimeDropdown } from '@/components/TimeDropdown';
import { SelectField } from '@/components/ui/SelectField';

interface VoiceAgent {
  id: string; name: string; description?: string | null; provider: string;
  providerAgentId?: string | null; providerSyncError?: string | null; voiceId?: string | null; language: string;
  firstMessage?: string | null; systemPrompt?: string | null;
  maxDurationSec: number; isActive: boolean;
}
interface VoiceCall {
  id: string; status: string; toNumber: string; direction: string;
  durationSec?: number | null; summary?: string | null; outcome?: string | null;
  recordingUrl?: string | null; transcript?: unknown; error?: string | null; isTest?: boolean;
  createdAt: string; agent?: { id: string; name: string } | null; lead?: { id: string; name: string } | null;
}
interface VoiceStatus { enabled: boolean; provider: string; configured: boolean; testMode?: boolean; testModeNumber?: string | null }
interface AiCfg { enabled: boolean; apiKey: string; provider: string }
interface VoiceCfg {
  enabled: boolean; provider: string; apiKey: string; fromNumber: string; elevenLabsPhoneNumberId: string; defaultAgentId: string;
  requireConsent: boolean; callWindowStart: string; callWindowEnd: string; callTimezone: string; dailyCap: string;
  testModeEnabled: boolean; testModeNumber: string;
}
interface OptOut { id: string; phoneNorm: string; reason?: string | null; createdAt: string }

type Tab = 'assistant' | 'agents' | 'calls' | 'settings';

const STATUS_STYLE: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-600', ringing: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-emerald-100 text-emerald-700',
  no_answer: 'bg-slate-100 text-slate-500', busy: 'bg-slate-100 text-slate-500',
  failed: 'bg-red-100 text-red-700', canceled: 'bg-slate-100 text-slate-500',
};
const INP = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 focus:border-[#6366F1]';
const fmtDur = (s?: number | null) => {
  if (!s || s < 0) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
const emptyAgent: Partial<VoiceAgent> = {
  name: '', description: '', voiceId: '', language: 'en',
  firstMessage: '', systemPrompt: '', providerAgentId: '', maxDurationSec: 300, isActive: true,
};

const TABS: { key: Tab; label: string }[] = [
  { key: 'assistant', label: 'Assistant' },
  { key: 'agents', label: 'Voice Agents' },
  { key: 'calls', label: 'Call Log' },
  { key: 'settings', label: 'AI Settings' },
];

export function AiHubPage() {
  const [tab, setTab] = useState<Tab>('assistant');
  const [status, setStatus] = useState<VoiceStatus | null>(null);
  const [agents, setAgents] = useState<VoiceAgent[]>([]);
  const [calls, setCalls] = useState<VoiceCall[]>([]);
  const [loading, setLoading] = useState(true);

  const [voices, setVoices] = useState<{ voice_id: string; voice_name: string; provider: string }[]>([]);
  const [editing, setEditing] = useState<Partial<VoiceAgent> | null>(null);
  const [saving, setSaving] = useState(false);
  const [callDetail, setCallDetail] = useState<VoiceCall | null>(null);
  const [placing, setPlacing] = useState<{ agentId: string; toNumber: string } | null>(null);
  // Replaces native window.confirm for agent delete — same styled dialog
  // used by the leads / trash / roles / teams surfaces.
  const [confirmDeleteAgent, setConfirmDeleteAgent] = useState<{ id: string; name: string } | null>(null);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  // AI config (both Assistant + Voice) — read/written via /settings/integrations
  const [ai, setAi] = useState<AiCfg>({ enabled: true, apiKey: '', provider: 'anthropic' });
  const [voice, setVoice] = useState<VoiceCfg>({
    enabled: false, provider: 'elevenlabs', apiKey: '', fromNumber: '', elevenLabsPhoneNumberId: '', defaultAgentId: '',
    requireConsent: true, callWindowStart: '09:00', callWindowEnd: '21:00', callTimezone: 'Asia/Kolkata', dailyCap: '',
    testModeEnabled: false, testModeNumber: '',
  });
  const [testingConn, setTestingConn] = useState(false);
  const [connResult, setConnResult] = useState<{ ok: boolean; account?: string; phone?: { number: string; label?: string; provider?: string }; error?: string } | null>(null);
  const [optOuts, setOptOuts] = useState<OptOut[]>([]);
  const [newDnc, setNewDnc] = useState('');
  const [showAiKey, setShowAiKey] = useState(false);
  const [showVoiceKey, setShowVoiceKey] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);
  // Import agents that already exist in the tenant's ElevenLabs account.
  type ProviderAgent = { agentId: string; name: string; imported: boolean };
  const [importOpen, setImportOpen] = useState(false);
  const [importList, setImportList] = useState<ProviderAgent[] | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSel, setImportSel] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [syncingCalls, setSyncingCalls] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, c, integ, o] = await Promise.all([
        api.get('/voice/status'),
        api.get('/voice/agents'),
        api.get('/voice/calls', { params: { limit: 50 } }),
        api.get('/settings/integrations'),
        api.get('/voice/optouts'),
      ]);
      setStatus(s.data);
      setAgents(a.data);
      setCalls(c.data);
      setOptOuts(o.data || []);
      const d = integ.data || {};
      if (d.ai) setAi({ enabled: d.ai.enabled !== false, apiKey: d.ai.apiKey ?? '', provider: d.ai.provider || 'anthropic' });
      if (d.voice) setVoice({
        enabled: d.voice.enabled === true, provider: d.voice.provider ?? 'elevenlabs',
        apiKey: d.voice.apiKey ?? '', fromNumber: d.voice.fromNumber ?? '',
        elevenLabsPhoneNumberId: d.voice.elevenLabsPhoneNumberId ?? '', defaultAgentId: d.voice.defaultAgentId ?? '',
        requireConsent: d.voice.requireConsent !== false,
        callWindowStart: d.voice.callWindowStart ?? '09:00', callWindowEnd: d.voice.callWindowEnd ?? '21:00',
        callTimezone: d.voice.callTimezone ?? 'Asia/Kolkata',
        dailyCap: d.voice.dailyCap ? String(d.voice.dailyCap) : '',
        testModeEnabled: d.voice.testModeEnabled === true, testModeNumber: d.voice.testModeNumber ?? '',
      });
    } catch {
      /* empty states render */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openImport = async () => {
    setImportOpen(true); setImportList(null); setImportError(null); setImportSel(new Set());
    try {
      const { data } = await api.get('/voice/agents/provider');
      setImportList(data || []);
    } catch (e: any) {
      setImportError(e?.response?.data?.message || 'Could not load agents from ElevenLabs.');
    }
  };
  // Pull call history (incl. calls made from the ElevenLabs dashboard) into the Call Log.
  const syncCalls = async () => {
    setSyncingCalls(true);
    try {
      const { data } = await api.post('/voice/calls/sync');
      if (!data?.agents) toast('error', 'No ElevenLabs agents here yet — import or create one first.');
      else if (data.added > 0) toast('success', `${data.added} call${data.added === 1 ? '' : 's'} added from ElevenLabs`);
      else toast('success', 'Call Log is up to date');
      if (data?.failed) toast('error', `${data.failed} call${data.failed === 1 ? '' : 's'} could not be loaded`);
      load();
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Could not sync calls');
    } finally { setSyncingCalls(false); }
  };

  const runImport = async () => {
    if (importSel.size === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post('/voice/agents/import', { agentIds: [...importSel] });
      const n = data?.imported ?? 0;
      if (n > 0) toast('success', `${n} agent${n === 1 ? '' : 's'} imported`);
      if (data?.failed?.length) toast('error', `${data.failed.length} could not be imported: ${data.failed[0].error}`);
      setImportOpen(false);
      load();
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Import failed');
    } finally { setImporting(false); }
  };

  // Provider voices for the agent picker (empty if key not set / provider down).
  useEffect(() => {
    api.get('/voice/voices').then(r => setVoices(r.data || [])).catch(() => setVoices([]));
  }, []);

  // ── Voice agent CRUD ──
  const saveAgent = async () => {
    if (!editing?.name?.trim()) { toast('error', 'Agent name is required'); return; }
    setSaving(true);
    try {
      const body = {
        name: editing.name, description: editing.description || undefined,
        voiceId: editing.voiceId || undefined, language: editing.language || 'en',
        firstMessage: editing.firstMessage || undefined, systemPrompt: editing.systemPrompt || undefined,
        providerAgentId: editing.providerAgentId || undefined,
        maxDurationSec: editing.maxDurationSec || 300, isActive: editing.isActive ?? true,
      };
      if (editing.id) await api.patch(`/voice/agents/${editing.id}`, body);
      else await api.post('/voice/agents', body);
      toast('success', editing.id ? 'Agent updated' : 'Agent created');
      setEditing(null); load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save agent');
    } finally { setSaving(false); }
  };
  // Opens the styled confirm modal. Actual delete happens in
  // confirmRemoveAgent below.
  const removeAgent = (agent: VoiceAgent) => {
    setConfirmDeleteAgent({ id: agent.id, name: agent.name || 'this voice agent' });
  };
  const confirmRemoveAgent = async () => {
    if (!confirmDeleteAgent) return;
    setConfirmDeleteLoading(true);
    try {
      await api.delete(`/voice/agents/${confirmDeleteAgent.id}`);
      toast('success', 'Agent deleted');
      setConfirmDeleteAgent(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete');
    } finally {
      setConfirmDeleteLoading(false);
    }
  };
  const syncAgent = async (id: string) => {
    try {
      const { data } = await api.post(`/voice/agents/${id}/sync`);
      toast(data?.synced ? 'success' : 'error', data?.synced ? 'Synced to provider' : (data?.error || 'Sync failed'));
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Sync failed');
    }
  };
  const placeCall = async () => {
    if (!placing?.toNumber?.trim()) { toast('error', 'Enter a phone number (E.164, e.g. +9198…)'); return; }
    setSaving(true);
    try {
      await api.post('/voice/calls', { agentId: placing.agentId, toNumber: placing.toNumber.trim() });
      toast('success', 'Call placed'); setPlacing(null); setTab('calls'); load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to place call');
    } finally { setSaving(false); }
  };

  // ── AI config save ──
  const saveAi = async () => {
    setSavingAi(true);
    try {
      // An empty key field means "leave the stored key alone" — never wipe a
      // saved key by accident. Removing a key is an explicit action (removeAiKey).
      await api.patch('/settings/integrations', { ai: { enabled: ai.enabled, provider: ai.provider, ...(ai.apiKey ? { apiKey: ai.apiKey } : {}) } });
      toast('success', 'AI Assistant saved'); load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save');
    } finally { setSavingAi(false); }
  };
  const saveVoice = async () => {
    setSavingVoice(true);
    try {
      await api.patch('/settings/integrations', {
        voice: {
          enabled: voice.enabled, provider: voice.provider, ...(voice.apiKey ? { apiKey: voice.apiKey } : {}),
          fromNumber: voice.fromNumber, elevenLabsPhoneNumberId: voice.elevenLabsPhoneNumberId,
          defaultAgentId: voice.defaultAgentId,
          requireConsent: voice.requireConsent, callWindowStart: voice.callWindowStart,
          callWindowEnd: voice.callWindowEnd, callTimezone: voice.callTimezone,
          dailyCap: voice.dailyCap ? Number(voice.dailyCap) : 0,
          testModeEnabled: voice.testModeEnabled, testModeNumber: voice.testModeNumber.trim(),
        },
      });
      toast('success', 'AI Voice Calls saved'); load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save');
    } finally { setSavingVoice(false); }
  };

  // Explicit key removal (the only way a stored key is cleared).
  const removeKey = async (section: 'ai' | 'voice') => {
    if (!window.confirm('Remove the saved API key? This cannot be undone.')) return;
    try {
      await api.patch('/settings/integrations', { [section]: { apiKey: '' } });
      toast('success', 'API key removed'); load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to remove key');
    }
  };

  // Master switches persist immediately (a kill-switch shouldn't need a separate
  // Save). PATCH merges per-field, so sending just { enabled } keeps the rest.
  const toggleAiEnabled = async (v: boolean) => {
    setAi(p => ({ ...p, enabled: v }));
    try {
      await api.patch('/settings/integrations', { ai: { enabled: v } });
      toast('success', v ? 'AI features enabled' : 'AI features disabled');
      load();
    } catch (err: any) {
      setAi(p => ({ ...p, enabled: !v })); // revert on failure
      toast('error', err?.response?.data?.message || 'Failed to update');
    }
  };
  const toggleVoiceEnabled = async (v: boolean) => {
    setVoice(p => ({ ...p, enabled: v }));
    try {
      await api.patch('/settings/integrations', { voice: { enabled: v } });
      toast('success', v ? 'AI Agent Calls enabled' : 'AI Agent Calls disabled');
      load();
    } catch (err: any) {
      setVoice(p => ({ ...p, enabled: !v }));
      toast('error', err?.response?.data?.message || 'Failed to update');
    }
  };

  // "Test connection": verifies the typed (or stored) key and, for ElevenLabs,
  // that the Phone Number ID is a registered number — without placing a call.
  const testConnection = async () => {
    setTestingConn(true); setConnResult(null);
    try {
      const { data } = await api.post('/voice/test-connection', {
        provider: voice.provider, apiKey: voice.apiKey, elevenLabsPhoneNumberId: voice.elevenLabsPhoneNumberId,
      });
      setConnResult(data);
      toast(data?.ok ? 'success' : 'error', data?.ok ? 'Connection OK' : (data?.error || 'Connection failed'));
    } catch (err: any) {
      const error = err?.response?.data?.message || 'Connection failed';
      setConnResult({ ok: false, error }); toast('error', error);
    } finally { setTestingConn(false); }
  };

  const addDnc = async () => {
    if (!newDnc.trim()) { toast('error', 'Enter a phone number'); return; }
    try {
      await api.post('/voice/optouts', { phone: newDnc.trim() });
      setNewDnc(''); toast('success', 'Added to do-not-call');
      api.get('/voice/optouts').then(r => setOptOuts(r.data || []));
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to add');
    }
  };
  const removeDnc = async (id: string) => {
    try {
      await api.delete(`/voice/optouts/${id}`);
      setOptOuts(prev => prev.filter(o => o.id !== id));
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to remove');
    }
  };

  // Overview ON/OFF mirrors each master switch (so it matches the toggles in
  // AI Settings). Readiness — vendor key present, provider configured — is shown
  // in the detail line, not by flipping the whole card off.
  const aiOn = ai.enabled;
  const voiceOn = !!status?.enabled;

  return (
    <DashboardLayout title="AI Assistant">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-[#6366F1] flex items-center gap-2">
              <Sparkles className="h-6 w-6" /> AI Assistant
            </h2>
            <p className="text-sm text-[#64748B] mt-1">All your AI in one place — drafts, smart alerts, and voice-call agents. Voice and script under your control.</p>
          </div>
          {tab === 'calls' && voice.provider === 'elevenlabs' && (
            <button onClick={syncCalls} disabled={syncingCalls} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-slate-700 hover:bg-gray-50 disabled:opacity-60">
              {syncingCalls ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Sync from ElevenLabs
            </button>
          )}
          {tab === 'agents' && (
            <div className="flex items-center gap-2">
              {voice.provider === 'elevenlabs' && (
                <button onClick={openImport} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-slate-700 hover:bg-gray-50">
                  <Download className="h-4 w-4" /> Import from ElevenLabs
                </button>
              )}
              <button onClick={() => setEditing({ ...emptyAgent })} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] text-white text-sm font-semibold hover:bg-[#5457e5]">
                <Plus className="h-4 w-4" /> New Agent
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex gap-1 flex-wrap">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? 'border-[#6366F1] text-[#6366F1]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" /></div>
        ) : tab === 'assistant' ? (
          <AssistantOverview aiOn={aiOn} voiceOn={voiceOn} voiceStatus={status} onGoSettings={() => setTab('settings')} />
        ) : tab === 'agents' ? (
          agents.length === 0 ? (
            <EmptyState icon={<Bot className="h-8 w-8" />} title="No agents yet" hint="Create your first AI caller with New Agent, or bring in the agents you already built with Import from ElevenLabs." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {agents.map(a => (
                <div key={a.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center"><Bot className="h-5 w-5 text-[#6366F1]" /></div>
                      <div>
                        <div className="font-semibold text-slate-800">{a.name}</div>
                        <div className="text-xs text-slate-400 capitalize">{a.provider} · {a.language}</div>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{a.isActive ? 'Active' : 'Off'}</span>
                  </div>
                  {a.description && <p className="text-sm text-slate-500 mt-3 line-clamp-2">{a.description}</p>}
                  {/* Provider sync status */}
                  <div className="mt-3">
                    {a.providerSyncError ? (
                      <span className="inline-flex items-center gap-1 text-xs text-red-600" title={a.providerSyncError}>
                        <AlertTriangle className="h-3.5 w-3.5" /> Sync failed
                      </span>
                    ) : a.providerAgentId ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Synced to provider</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400"><AlertTriangle className="h-3.5 w-3.5" /> Not synced</span>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2 flex-wrap">
                    <button onClick={() => setPlacing({ agentId: a.id, toNumber: '' })} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6366F1] hover:underline"><PhoneCall className="h-4 w-4" /> Call</button>
                    <span className="text-slate-200">·</span>
                    <button onClick={() => syncAgent(a.id)} className="text-sm text-slate-600 hover:text-slate-900">Sync</button>
                    <span className="text-slate-200">·</span>
                    <button onClick={() => setEditing(a)} className="text-sm text-slate-600 hover:text-slate-900">Edit</button>
                    <span className="text-slate-200">·</span>
                    <button onClick={() => removeAgent(a)} className="text-sm text-red-500 hover:text-red-700">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : tab === 'calls' ? (
          calls.length === 0 ? (
            <EmptyState icon={<PhoneCall className="h-8 w-8" />} title="No calls yet" hint={voice.provider === 'elevenlabs' ? 'Place a call from any agent, or use Sync from ElevenLabs to bring in calls made there.' : 'Place a call from any agent — the history will appear here.'} />
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#6366F1] text-white text-left">
                      <th className="px-4 py-3 font-semibold">When</th>
                      <th className="px-4 py-3 font-semibold">To</th>
                      <th className="px-4 py-3 font-semibold">Lead</th>
                      <th className="px-4 py-3 font-semibold">Agent</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.map(c => (
                      <tr key={c.id} onClick={() => setCallDetail(c)} className="border-b border-gray-50 hover:bg-indigo-50/40 cursor-pointer">
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDateTime(c.createdAt)}</td>
                        <td className="px-4 py-3 text-slate-700 font-medium tabular-nums">{c.toNumber}</td>
                        <td className="px-4 py-3 text-slate-600">{c.lead?.name ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{c.agent?.name ?? '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[c.status] ?? 'bg-slate-100 text-slate-600'}`}>{c.status.replace('_', ' ')}</span>
                          {c.isTest && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700">Test</span>}
                          {(c as any).triggerType === 'imported' && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500">From ElevenLabs</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{fmtDur(c.durationSec)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          <AiSettingsTab
            ai={ai} setAi={setAi} voice={voice} setVoice={setVoice}
            showAiKey={showAiKey} setShowAiKey={setShowAiKey}
            showVoiceKey={showVoiceKey} setShowVoiceKey={setShowVoiceKey}
            savingAi={savingAi} savingVoice={savingVoice} onSaveAi={saveAi} onSaveVoice={saveVoice}
            onToggleAi={toggleAiEnabled} onToggleVoice={toggleVoiceEnabled}
            onTestConnection={testConnection} testingConn={testingConn} connResult={connResult} onRemoveKey={removeKey}
            optOuts={optOuts} newDnc={newDnc} setNewDnc={setNewDnc} onAddDnc={addDnc} onRemoveDnc={removeDnc}
          />
        )}
      </div>

      {/* Import from ElevenLabs */}
      {importOpen && (
        <Drawer title="Import from ElevenLabs" onClose={() => setImportOpen(false)}>
          {importError ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{importError}</div>
          ) : importList === null ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
          ) : importList.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No agents found in your ElevenLabs account.</p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Pick the agents to add. Their name, script, first message, voice and language are copied; calls use the same agent in ElevenLabs.</p>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{importList.length} agent{importList.length === 1 ? '' : 's'} in ElevenLabs</span>
                {importList.some(a => !a.imported) && (
                  <button className="font-medium text-[#6366F1] hover:underline" onClick={() => {
                    const avail = importList.filter(a => !a.imported).map(a => a.agentId);
                    setImportSel(importSel.size === avail.length ? new Set() : new Set(avail));
                  }}>
                    {importSel.size === importList.filter(a => !a.imported).length ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </div>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {importList.map(a => (
                  <label key={a.agentId} className={`flex items-center gap-3 px-3 py-2.5 ${a.imported ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      disabled={a.imported}
                      checked={a.imported || importSel.has(a.agentId)}
                      onChange={() => setImportSel(prev => { const n = new Set(prev); if (n.has(a.agentId)) n.delete(a.agentId); else n.add(a.agentId); return n; })}
                      className="h-4 w-4 rounded border-slate-300 text-[#6366F1] focus:ring-[#6366F1]"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">{a.name}</p>
                      <p className="truncate font-mono text-[11px] text-slate-400">{a.agentId}</p>
                    </div>
                    {a.imported && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">Already added</span>}
                  </label>
                ))}
              </div>
              <button onClick={runImport} disabled={importSel.size === 0 || importing}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#6366F1] py-2.5 text-sm font-semibold text-white hover:bg-[#5457e5] disabled:opacity-50">
                {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                Import {importSel.size > 0 ? importSel.size : ''} agent{importSel.size === 1 ? '' : 's'}
              </button>
            </div>
          )}
        </Drawer>
      )}

      {/* Agent editor drawer */}
      {editing && (
        <Drawer title={editing.id ? 'Edit Agent' : 'New Agent'} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Field label="Name *"><input value={editing.name ?? ''} onChange={e => setEditing({ ...editing, name: e.target.value })} className={INP} placeholder="Welcome Caller" /></Field>
            <Field label="Description"><input value={editing.description ?? ''} onChange={e => setEditing({ ...editing, description: e.target.value })} className={INP} placeholder="Calls new leads within 60s" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Voice">
                {voices.length > 0 ? (
                  <SelectField value={editing.voiceId ?? ''} onChange={e => setEditing({ ...editing, voiceId: e.target.value })} className={INP}>
                    <option value="">Default (11labs-Adrian)</option>
                    {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.voice_name} ({v.provider})</option>)}
                  </SelectField>
                ) : (
                  <input value={editing.voiceId ?? ''} onChange={e => setEditing({ ...editing, voiceId: e.target.value })} className={INP} placeholder="11labs-Adrian" />
                )}
              </Field>
              <Field label="Language"><input value={editing.language ?? 'en'} onChange={e => setEditing({ ...editing, language: e.target.value })} className={INP} placeholder="en" /></Field>
            </div>
            <p className="-mt-2 text-[11px] text-slate-400">{voices.length > 0 ? 'Voices loaded from your Retell account. Leave as Default if unsure.' : <>Add & save your Retell key first to load the voice list. Meanwhile use a Retell id like <code className="bg-slate-100 px-1 rounded">11labs-Adrian</code> (not a raw ElevenLabs id), or leave blank.</>}</p>
            <Field label="Provider Agent ID"><input value={editing.providerAgentId ?? ''} onChange={e => setEditing({ ...editing, providerAgentId: e.target.value })} className={INP} placeholder="agent_… (Retell)" /></Field>
            <Field label="First message (opening line — AI disclosure)"><textarea value={editing.firstMessage ?? ''} onChange={e => setEditing({ ...editing, firstMessage: e.target.value })} className={`${INP} min-h-[64px]`} placeholder="Hi, this is an AI assistant from Acme…" /></Field>
            <Field label="Script / system prompt (what to say)"><textarea value={editing.systemPrompt ?? ''} onChange={e => setEditing({ ...editing, systemPrompt: e.target.value })} className={`${INP} min-h-[140px]`} placeholder="You are a friendly counselor. Goal: qualify interest, budget and timeline, then offer to connect a human…" /></Field>
            <div className="grid grid-cols-2 gap-3 items-end">
              <Field label="Max duration (sec)"><input type="number" value={editing.maxDurationSec ?? 300} onChange={e => setEditing({ ...editing, maxDurationSec: Number(e.target.value) })} className={INP} /></Field>
              <label className="flex items-center gap-2 text-sm text-slate-700 pb-2"><input type="checkbox" checked={editing.isActive ?? true} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} className="h-4 w-4 rounded" /> Active</label>
            </div>
          </div>
          <div className="flex gap-2 pt-5">
            <button onClick={saveAgent} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#6366F1] text-white text-sm font-semibold hover:bg-[#5457e5] disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-slate-600 hover:bg-gray-50">Cancel</button>
          </div>
        </Drawer>
      )}

      {/* Place-call drawer */}
      {placing && (
        <Drawer title="Place AI call" onClose={() => setPlacing(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Test call — the AI agent will call this number. Use E.164 format (with country code).</p>
            <Field label="Phone number"><input value={placing.toNumber} onChange={e => setPlacing({ ...placing, toNumber: e.target.value })} className={INP} placeholder="+919876543210" /></Field>
          </div>
          <div className="flex gap-2 pt-5">
            <button onClick={placeCall} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#6366F1] text-white text-sm font-semibold hover:bg-[#5457e5] disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />} Call now</button>
            <button onClick={() => setPlacing(null)} className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-slate-600 hover:bg-gray-50">Cancel</button>
          </div>
        </Drawer>
      )}

      {/* Call detail drawer */}
      {callDetail && (
        <Drawer title="Call detail" onClose={() => setCallDetail(null)}>
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Info label="To" value={callDetail.toNumber} />
              <Info label="Status" value={callDetail.status.replace('_', ' ')} />
              <Info label="Lead" value={callDetail.lead?.name ?? '—'} />
              <Info label="Agent" value={callDetail.agent?.name ?? '—'} />
              <Info label="Duration" value={fmtDur(callDetail.durationSec)} />
              <Info label="Outcome" value={callDetail.outcome ?? '—'} />
            </div>
            {callDetail.recordingUrl && (<div><div className="text-xs font-medium text-slate-400 mb-1">Recording</div><audio controls src={callDetail.recordingUrl} className="w-full" /></div>)}
            {callDetail.summary && (<div><div className="text-xs font-medium text-slate-400 mb-1">Summary</div><p className="text-slate-700 bg-slate-50 rounded-lg p-3">{callDetail.summary}</p></div>)}
            {callDetail.error && (<div><div className="text-xs font-medium text-red-400 mb-1">Error</div><p className="text-red-600 bg-red-50 rounded-lg p-3">{callDetail.error}</p></div>)}
            {!!callDetail.transcript && (
              <div>
                <div className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> Transcript</div>
                <pre className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap max-h-72 overflow-auto">{typeof callDetail.transcript === 'string' ? callDetail.transcript : JSON.stringify(callDetail.transcript, null, 2)}</pre>
              </div>
            )}
          </div>
        </Drawer>
      )}

      {/* Styled delete confirmation for voice agents — swapped in from
          native window.confirm for consistency with the rest of the app. */}
      <ConfirmModal
        isOpen={!!confirmDeleteAgent}
        title="Delete Voice Agent?"
        message={`Are you sure you want to delete "${confirmDeleteAgent?.name ?? 'this agent'}"? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={confirmRemoveAgent}
        onCancel={() => setConfirmDeleteAgent(null)}
        isLoading={confirmDeleteLoading}
      />
    </DashboardLayout>
  );
}

function AssistantOverview({ aiOn, voiceOn, voiceStatus, onGoSettings }: { aiOn: boolean; voiceOn: boolean; voiceStatus: VoiceStatus | null; onGoSettings: () => void }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <StatusCard icon={<Sparkles className="h-5 w-5" />} title="AI Assistant" on={aiOn}
          detail={aiOn ? 'Enabled' : 'Turned off'} onFix={onGoSettings} />
        <StatusCard icon={<Phone className="h-5 w-5" />} title="AI Voice Calls" on={voiceOn}
          detail={voiceStatus?.enabled ? (voiceStatus?.configured ? 'Retell connected' : 'Key/from-number missing') : 'Master switch OFF'} onFix={onGoSettings} />
      </div>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">What AI does</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <Feature icon={<MessageSquare className="h-5 w-5 text-[#6366F1]" />} title="Draft with AI" desc="Write a lead's WhatsApp message with AI." />
          <Feature icon={<Bell className="h-5 w-5 text-amber-500" />} title="Smart alerts" desc="AI 'who to call today' suggestions on the dashboard." />
          <Feature icon={<TrendingUp className="h-5 w-5 text-emerald-500" />} title="Lead insights" desc="AI-driven scoring and next-action hints." />
          <Feature icon={<PhoneCall className="h-5 w-5 text-[#6366F1]" />} title="Voice agents" desc="AI calls leads and talks to them." />
          <Feature icon={<Bot className="h-5 w-5 text-slate-500" />} title="2-way conversation" desc="Replies, qualifies, then hands over to a human." />
        </div>
      </div>
    </div>
  );
}

function AiSettingsTab({ ai, setAi, voice, setVoice, showAiKey, setShowAiKey, showVoiceKey, setShowVoiceKey, savingAi, savingVoice, onSaveAi, onSaveVoice, onToggleAi, onToggleVoice, onTestConnection, testingConn, connResult, onRemoveKey, optOuts, newDnc, setNewDnc, onAddDnc, onRemoveDnc }: {
  ai: AiCfg; setAi: (v: AiCfg) => void; voice: VoiceCfg; setVoice: (v: VoiceCfg) => void;
  onTestConnection: () => void; testingConn: boolean; onRemoveKey: (section: 'ai' | 'voice') => void;
  connResult: { ok: boolean; account?: string; phone?: { number: string; label?: string; provider?: string }; error?: string } | null;
  showAiKey: boolean; setShowAiKey: (v: boolean) => void; showVoiceKey: boolean; setShowVoiceKey: (v: boolean) => void;
  savingAi: boolean; savingVoice: boolean; onSaveAi: () => void; onSaveVoice: () => void;
  onToggleAi: (v: boolean) => void; onToggleVoice: (v: boolean) => void;
  optOuts: OptOut[]; newDnc: string; setNewDnc: (v: string) => void; onAddDnc: () => void; onRemoveDnc: (id: string) => void;
}) {
  // Eye toggle: the loaded key is masked (************). When revealing, fetch
  // the real decrypted value from the backend once, then show it. If the user
  // has typed a new (unsaved) key, just toggle visibility on what they typed.
  const isMasked = (v: string) => v.startsWith('*');
  const revealAi = async () => {
    if (!showAiKey && isMasked(ai.apiKey || '')) {
      try { const { data } = await api.get('/settings/integrations/ai/apiKey/reveal'); setAi({ ...ai, apiKey: data?.value ?? '' }); }
      catch (err: any) { toast('error', err?.response?.data?.message || 'Could not reveal key'); return; }
    }
    setShowAiKey(!showAiKey);
  };
  const revealVoice = async () => {
    if (!showVoiceKey && isMasked(voice.apiKey || '')) {
      try { const { data } = await api.get('/settings/integrations/voice/apiKey/reveal'); setVoice({ ...voice, apiKey: data?.value ?? '' }); }
      catch (err: any) { toast('error', err?.response?.data?.message || 'Could not reveal key'); return; }
    }
    setShowVoiceKey(!showVoiceKey);
  };
  // Jump the client from the guide straight to the ElevenLabs Phone Number ID
  // field. The field only renders under the ElevenLabs provider, so switch to it
  // first, then (after the re-render) scroll it into view, focus and flash it.
  const jumpToElPhoneField = () => {
    setVoice(voice.provider === 'elevenlabs' ? voice : { ...voice, provider: 'elevenlabs' });
    setTimeout(() => {
      const el = document.getElementById('el-phone-number-id') as HTMLInputElement | null;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
      el.classList.add('ring-2', 'ring-[#6366F1]');
      setTimeout(() => el.classList.remove('ring-2', 'ring-[#6366F1]'), 1600);
    }, 80);
  };
  return (
    <div className="space-y-5 max-w-3xl">
      {/* AI Assistant config */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center"><Sparkles className="h-4 w-4 text-indigo-600" /></div>
          <div>
            <h3 className="font-semibold text-slate-800">AI Assistant</h3>
            <p className="text-xs text-slate-400">Draft with AI, smart alerts, insights. Use your own Claude, ChatGPT, or Gemini key.</p>
          </div>
        </div>
        <Toggle label="Enable AI features" hint="Turn off to disable all AI suggestions for your workspace. Saved instantly." checked={ai.enabled} onChange={onToggleAi} />
        {(() => {
          const META: Record<string, { label: string; keyLabel: string; placeholder: string; help: string }> = {
            anthropic: { label: 'Claude (Anthropic)', keyLabel: 'Anthropic API key', placeholder: 'sk-ant-xxxxxxxx', help: 'console.anthropic.com → API Keys. Stored encrypted. Blank = platform shared key.' },
            openai: { label: 'ChatGPT (OpenAI)', keyLabel: 'OpenAI API key', placeholder: 'sk-xxxxxxxx', help: 'platform.openai.com → API keys. Stored encrypted. Required (no platform fallback).' },
            gemini: { label: 'Gemini (Google)', keyLabel: 'Gemini API key', placeholder: 'AIza-xxxxxxxx', help: 'aistudio.google.com → Get API key. Stored encrypted. Required (no platform fallback).' },
          };
          const m = META[ai.provider] ?? META.anthropic;
          return (
            <>
              <div className="mt-4">
                <label className="block text-xs font-normal text-slate-500 mb-1.5">AI provider</label>
                <SelectField
                  value={ai.provider}
                  onChange={e => { if (e.target.value !== ai.provider) setAi({ ...ai, provider: e.target.value, apiKey: '' }); }}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="anthropic">Claude (Anthropic)</option>
                  <option value="openai">ChatGPT (OpenAI)</option>
                  <option value="gemini">Gemini (Google)</option>
                </SelectField>
              </div>
              <div className="mt-4">
                <label className="block text-xs font-normal text-slate-500 mb-1.5">{m.keyLabel}</label>
                <KeyInput value={ai.apiKey} onChange={v => setAi({ ...ai, apiKey: v })} show={showAiKey} onToggle={revealAi} placeholder={m.placeholder} />
                <p className="text-xs text-slate-500 mt-1">
                  {m.help}
                  {isMasked(ai.apiKey || '') && <> · <button type="button" onClick={() => onRemoveKey('ai')} className="text-red-600 hover:underline">Remove saved key</button></>}
                </p>
              </div>
            </>
          );
        })()}
        <div className="flex justify-end mt-4">
          <button onClick={onSaveAi} disabled={savingAi} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">{savingAi && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </div>

      {/* AI Voice config */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center"><Phone className="h-4 w-4 text-indigo-600" /></div>
          <div>
            <h3 className="font-semibold text-slate-800">AI Voice Calls</h3>
            <p className="text-xs text-slate-400">
              {voice.provider === 'elevenlabs'
                ? 'Provider: ElevenLabs Conversational AI (dials over your registered Twilio number).'
                : 'Provider: Retell (legacy).'} Master switch toggles all AI calling on/off.
            </p>
          </div>
        </div>

        {/* Self-serve setup guide — lets a client wire up Retell / ElevenLabs
            without hand-holding. Kept collapsible so it doesn't crowd the form. */}
        <details className="mt-1 mb-5 rounded-xl border border-indigo-100 bg-indigo-50/40">
          <summary className="cursor-pointer select-none list-none px-4 py-3 text-sm font-semibold text-slate-800 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-indigo-600" />
            Setup guide — connect ElevenLabs (or legacy Retell)
            <span className="ml-auto text-xs font-normal text-slate-400">click to expand / collapse</span>
          </summary>

          <div className="px-4 pb-4 pt-1 space-y-5 text-sm text-slate-600">
            <p>
              AI Voice Calls let an AI agent phone your leads, talk to them, and log the call
              (outcome, recording, transcript) back on the lead. Pick <b>one</b> provider, add its
              API key and a phone number, create an agent, then place a test call. Calls are billed
              by the provider (pay per minute); your API key is stored encrypted.
            </p>

            {/* ElevenLabs */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h4 className="font-semibold text-slate-800 mb-2">Option A — ElevenLabs Conversational AI (recommended)</h4>
              <ol className="list-decimal pl-5 space-y-1.5">
                <li>Create an account at <span className="font-medium">elevenlabs.io</span> with Conversational AI enabled, and add billing.</li>
                <li>Register your phone number: <span className="font-medium">Conversational AI → Phone Numbers → import your Twilio number</span>. ElevenLabs issues a <span className="font-medium">Phone Number ID</span> (starts with <code className="bg-slate-100 px-1 rounded">phnum_</code>).</li>
                <li>Copy your API key from <span className="font-medium">ElevenLabs → Profile → API key</span> (starts with <code className="bg-slate-100 px-1 rounded">sk_</code>).</li>
                <li>On this page: set <span className="font-medium">Provider = ElevenLabs</span>, paste the key into <span className="font-medium">ElevenLabs API key</span>, and paste the id into{' '}
                  <button
                    type="button"
                    onClick={jumpToElPhoneField}
                    className="font-medium text-[#6366F1] underline decoration-dotted underline-offset-2 hover:text-[#4F46E5]"
                    title="Go to the ElevenLabs Phone Number ID field"
                  >
                    ElevenLabs Phone Number ID ↓
                  </button>. Turn on <span className="font-medium">Enable AI Agent Calls</span> and click <span className="font-medium">Save</span>.</li>
                <li>Open the <span className="font-medium">Voice Agents</span> tab → <span className="font-medium">New Agent</span>, set voice, language, first message and script, Save, then <span className="font-medium">Sync</span> (creates the agent in ElevenLabs).</li>
                <li>
                  In ElevenLabs, open your agent's <span className="font-medium">post-call webhook</span> settings and paste this URL:
                  <span className="mt-1.5 flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">{webhookUrl('elevenlabs')}</code>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard?.writeText(webhookUrl('elevenlabs')); toast('success', 'ElevenLabs webhook URL copied'); }}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] whitespace-nowrap"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  </span>
                </li>
                <li>Test it from the <span className="font-medium">Voice Agents</span> tab with <span className="font-medium">Call</span>.</li>
              </ol>
            </div>
            {/* Retell */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h4 className="font-semibold text-slate-800 mb-2">Option B — Retell (legacy, no in-call tools)</h4>
              <ol className="list-decimal pl-5 space-y-1.5">
                <li>Create an account at <span className="font-medium">dashboard.retellai.com</span> and add billing.</li>
                <li>Get a phone number: in Retell go to <span className="font-medium">Phone Numbers</span> and buy a number (or connect your Twilio number). This is your caller ID.</li>
                <li>Copy your API key from <span className="font-medium">Retell → API Keys</span>.</li>
                <li>On this page: set <span className="font-medium">Provider = Retell</span>, paste the key into <span className="font-medium">Retell API key</span>, and put your Retell number in <span className="font-medium">From number (caller ID)</span> in E.164 format (e.g. <code className="bg-slate-100 px-1 rounded">+14155551234</code> or <code className="bg-slate-100 px-1 rounded">+919876543210</code>). Turn on <span className="font-medium">Enable AI Agent Calls</span> and click <span className="font-medium">Save</span>.</li>
                <li>Open the <span className="font-medium">Voice Agents</span> tab → <span className="font-medium">New Agent</span>. Set the name, voice, language, first message and script (system prompt). Save, then click <span className="font-medium">Sync</span> — this creates the agent in Retell and registers the webhook automatically.</li>
                <li>
                  (Manual alternative) If you built the agent inside Retell yourself, paste its <code className="bg-slate-100 px-1 rounded">agent_…</code> id into <span className="font-medium">Default provider agent ID</span>, and paste this webhook URL into that agent's webhook setting in Retell:
                  <span className="mt-1.5 flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">{webhookUrl('retell')}</code>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard?.writeText(webhookUrl('retell')); toast('success', 'Retell webhook URL copied'); }}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] whitespace-nowrap"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  </span>
                  <span className="mt-1 block text-xs text-slate-400">Not needed if you created the agent here and clicked Sync — the webhook is registered automatically.</span>
                </li>
                <li>Test it: on the <span className="font-medium">Voice Agents</span> tab click <span className="font-medium">Call</span>, enter a number in E.164, and confirm the call connects and appears under <span className="font-medium">Calls</span>.</li>
              </ol>
            </div>


            {/* Before your first call */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
              <h4 className="font-semibold text-amber-900 mb-2">Before your first call</h4>
              <ul className="list-disc pl-5 space-y-1.5 text-amber-900/90">
                <li>Use a real phone number as the caller ID — an email address will not place calls.</li>
                <li>Compliance guardrails (below) can block a call: the lead must have consent, must not be opted out or on the do-not-call list, and the call must fall within your calling hours and daily cap.</li>
                <li>Enter every number in E.164 format (country code + number, no spaces), e.g. <code className="bg-white px-1 rounded border border-amber-200">+919876543210</code>.</li>
                <li>Keep the <span className="font-medium">Enable AI Agent Calls</span> master switch on — turning it off stops all calls instantly.</li>
              </ul>
            </div>
          </div>
        </details>

        <Toggle label="Enable AI Agent Calls" hint="Master switch — turning it off stops all AI calls immediately. Saved instantly." checked={voice.enabled} onChange={onToggleVoice} />
        <div className="mt-4">
          <label className="block text-xs font-normal text-slate-500 mb-1.5">Provider</label>
          <SelectField value={voice.provider} onChange={e => setVoice({ ...voice, provider: e.target.value })} className={INP}>
            <option value="elevenlabs">ElevenLabs (recommended)</option>
            {voice.provider === 'retell' && <option value="retell">Retell (legacy)</option>}
          </SelectField>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-normal text-slate-500 mb-1.5">{voice.provider === 'elevenlabs' ? 'ElevenLabs' : 'Retell'} API key</label>
          <KeyInput value={voice.apiKey} onChange={v => setVoice({ ...voice, apiKey: v })} show={showVoiceKey} onToggle={revealVoice} placeholder={voice.provider === 'elevenlabs' ? 'sk_xxxxxxxx' : 'key_xxxxxxxx'} />
          <p className="text-xs text-slate-500 mt-1">
            {voice.provider === 'elevenlabs' ? 'ElevenLabs dashboard → Profile → API key.' : 'Retell dashboard → API Keys.'} Stored encrypted.
            {isMasked(voice.apiKey || '') && <> · <button type="button" onClick={() => onRemoveKey('voice')} className="text-red-600 hover:underline">Remove saved key</button></>}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {voice.provider === 'elevenlabs' ? (
            <div>
              <label className="block text-xs font-normal text-slate-500 mb-1.5">ElevenLabs Phone Number ID</label>
              <input id="el-phone-number-id" value={voice.elevenLabsPhoneNumberId} onChange={e => setVoice({ ...voice, elevenLabsPhoneNumberId: e.target.value })} className={INP} placeholder="phnum_… (from ElevenLabs)" />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-normal text-slate-500 mb-1.5">From number (caller ID)</label>
              <input value={voice.fromNumber} onChange={e => setVoice({ ...voice, fromNumber: e.target.value })} className={INP} placeholder="+14155551234" />
            </div>
          )}
          <div>
            <label className="block text-xs font-normal text-slate-500 mb-1.5">Default provider agent ID</label>
            <input value={voice.defaultAgentId} onChange={e => setVoice({ ...voice, defaultAgentId: e.target.value })} className={INP} placeholder="agent_… (fallback)" />
          </div>
        </div>
        {voice.provider === 'elevenlabs' && (
          <p className="text-[11px] text-slate-400 mt-1">Register your Twilio number in ElevenLabs (Conversational AI → Phone Numbers) — it issues the <b>Phone Number ID</b> to paste above.</p>
        )}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onTestConnection}
            disabled={testingConn}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-50"
          >
            {testingConn ? <Loader2 className="h-4 w-4 animate-spin" /> : <Webhook className="h-4 w-4" />} Test connection
          </button>
          {connResult && (
            <span className={`inline-flex items-center gap-1.5 text-xs ${connResult.ok ? 'text-emerald-700' : 'text-red-600'}`}>
              {connResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {connResult.ok
                ? `Connected${connResult.account ? ` · ${connResult.account}` : ''}${connResult.phone ? ` · number ${connResult.phone.number}${connResult.phone.provider ? ` (${connResult.phone.provider})` : ''}` : ''}`
                : connResult.error}
            </span>
          )}
        </div>
        {/* Provider webhook URL — auto-set on sync, but shown so vendors can paste it manually */}
        <div className="mt-4">
          <label className="block text-xs font-normal text-slate-500 mb-1.5">Provider webhook URL</label>
          <div className="flex items-center gap-2">
            <input readOnly value={webhookUrl(voice.provider)} onFocus={e => e.currentTarget.select()} className={`${INP} bg-slate-50 font-mono text-xs`} />
            <button
              type="button"
              onClick={() => { navigator.clipboard?.writeText(webhookUrl(voice.provider)); toast('success', 'Webhook URL copied'); }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] whitespace-nowrap"
            >
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Agents auto-set this on sync. Same URL for everyone — paste it in your Retell dashboard only if you set an agent up manually.</p>
        </div>
        {/* Test Mode */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <PhoneCall className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">Test Mode</h4>
          </div>
          <Toggle
            label="Send every AI call to a test number"
            hint="While on, all calls (manual, workflow, API) ring the test number below instead of the lead. The lead's details still go to the agent, so you can hear the real script. Consent, do-not-call and calling-hours checks are skipped because no lead is dialed."
            checked={voice.testModeEnabled}
            onChange={v => setVoice({ ...voice, testModeEnabled: v })}
          />
          <div className="mt-3 max-w-sm">
            <label className="block text-xs font-normal text-slate-500 mb-1.5">Test number (E.164)</label>
            <input value={voice.testModeNumber} onChange={e => setVoice({ ...voice, testModeNumber: e.target.value })} className={INP} placeholder="+919876543210" />
          </div>
          {voice.testModeEnabled && !voice.testModeNumber.trim() && (
            <p className="text-[11px] text-amber-600 mt-2">Add a test number, or calls will be refused while Test Mode is on.</p>
          )}
        </div>

        {/* Compliance guardrails */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">Compliance guardrails</h4>
          </div>
          <Toggle label="Require consent" hint="Only call leads whose consent is given (GDPR/TCPA safe)." checked={voice.requireConsent} onChange={v => setVoice({ ...voice, requireConsent: v })} />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs font-normal text-slate-500 mb-1.5">Calling hours start</label>
              <TimeDropdown value={voice.callWindowStart} onChange={v => setVoice({ ...voice, callWindowStart: v })} />
            </div>
            <div>
              <label className="block text-xs font-normal text-slate-500 mb-1.5">Calling hours end</label>
              <TimeDropdown value={voice.callWindowEnd} onChange={v => setVoice({ ...voice, callWindowEnd: v })} />
            </div>
            <div>
              <label className="block text-xs font-normal text-slate-500 mb-1.5">Fallback timezone</label>
              <input value={voice.callTimezone} onChange={e => setVoice({ ...voice, callTimezone: e.target.value })} className={INP} placeholder="Asia/Kolkata" />
              <p className="text-[11px] text-slate-400 mt-1">Hours are checked in each lead&apos;s own timezone (lead override → country). This is used only when neither is known.</p>
            </div>
            <div>
              <label className="block text-xs font-normal text-slate-500 mb-1.5">Daily cap (0 = unlimited)</label>
              <input type="number" min={0} value={voice.dailyCap} onChange={e => setVoice({ ...voice, dailyCap: e.target.value })} className={INP} placeholder="0" />
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Calls outside calling hours, without consent, on the do-not-call list, or over the cap are skipped automatically.</p>
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onSaveVoice} disabled={savingVoice} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">{savingVoice && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </div>

      {/* Do-not-call list */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-rose-100 flex items-center justify-center"><Ban className="h-4 w-4 text-rose-600" /></div>
          <div>
            <h3 className="font-semibold text-slate-800">Do-not-call list</h3>
            <p className="text-xs text-slate-400">AI will never call these numbers. Opt-outs during a call are added here automatically.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <input value={newDnc} onChange={e => setNewDnc(e.target.value)} onKeyDown={e => e.key === 'Enter' && onAddDnc()} className={INP} placeholder="+919876543210" />
          <button onClick={onAddDnc} className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 whitespace-nowrap"><Plus className="h-4 w-4" /> Add</button>
        </div>
        {optOuts.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">The list is empty.</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {optOuts.map(o => (
              <div key={o.id} className="flex items-center gap-3 py-2.5">
                <span className="font-medium text-slate-700 tabular-nums flex-1">{o.phoneNorm}</span>
                {o.reason && <span className="text-xs text-slate-400">{o.reason}</span>}
                <button onClick={() => onRemoveDnc(o.id)} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* External trigger (connect anywhere) */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center"><Webhook className="h-4 w-4 text-indigo-600" /></div>
          <div>
            <h3 className="font-semibold text-slate-800">Trigger a call from anywhere</h3>
            <p className="text-xs text-slate-400">Trigger a call from a website form or any external system using an API key.</p>
          </div>
        </div>
        <pre className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3 overflow-x-auto"><code>{`POST ${apiOrigin()}/api/v1/voice/call
X-API-Key: <your-api-key>
Content-Type: application/json

{
  "phone": "+919876543210",
  "name": "Rahul",
  "consent": true,
  "agentId": "<optional>",
  "source": "landing-page"
}`}</code></pre>
        <p className="text-[11px] text-slate-400 mt-2">
          The lead is found or created by phone; <code className="bg-slate-100 px-1 rounded">consent:true</code> records consent. All guardrails (consent/DNC/hours/cap) still apply. Create an API key under <b>Administrator → Developer</b>.
        </p>
      </div>
    </div>
  );
}

function apiOrigin(): string {
  const env = (import.meta as any).env?.VITE_API_URL as string | undefined;
  if (env) return env.replace(/\/$/, '');
  return window.location.origin;
}
function webhookUrl(provider = 'retell'): string {
  return `${apiOrigin()}/api/voice/webhook/${provider}`;
}

// ── small shared pieces ──
function StatusCard({ icon, title, on, detail, onFix }: { icon: React.ReactNode; title: string; on: boolean; detail: string; onFix: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${on ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>{icon}</div>
      <div className="flex-1">
        <div className="font-semibold text-slate-800 flex items-center gap-2">{title}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{on ? 'ON' : 'OFF'}</span>
        </div>
        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
          {on ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />} {detail}
        </div>
      </div>
      {!on && <button onClick={onFix} className="text-sm font-medium text-[#6366F1] hover:underline whitespace-nowrap">Set up</button>}
    </div>
  );
}
function Feature({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="flex gap-3">
      <div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <div className="text-sm font-medium text-slate-700">{title}</div>
        <div className="text-xs text-slate-400">{desc}</div>
      </div>
    </div>
  );
}
function Toggle({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-400">{hint}</p>
      </div>
      <button type="button" onClick={() => onChange(!checked)} className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${checked ? 'bg-indigo-500' : 'bg-slate-300'}`}>
        <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  );
}
function KeyInput({ value, onChange, show, onToggle, placeholder }: { value: string; onChange: (v: string) => void; show: boolean; onToggle: () => void; placeholder: string }) {
  return (
    <div className="relative">
      <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} className={`${INP} pr-10`} placeholder={placeholder} autoComplete="off" />
      <button type="button" onClick={onToggle} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
function Drawer({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-slate-400"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (<label className="block"><span className="block text-xs font-medium text-slate-500 mb-1.5">{label}</span>{children}</label>);
}
function Info({ label, value }: { label: string; value: string }) {
  return (<div><div className="text-xs font-medium text-slate-400">{label}</div><div className="text-slate-700 font-medium capitalize">{value}</div></div>);
}
function EmptyState({ icon, title, hint }: { icon: React.ReactNode; title: string; hint: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="h-16 w-16 rounded-2xl bg-indigo-50 text-[#6366F1] flex items-center justify-center mb-4">{icon}</div>
      <div className="font-semibold text-slate-700">{title}</div>
      <div className="text-sm text-slate-400 mt-1 max-w-xs">{hint}</div>
    </div>
  );
}

export default AiHubPage;
